import { cn } from "cn";
import { motion, type Transition } from "motion/react";
import { type PropsWithChildren, useEffect, useRef, useState } from "react";

type AnimateChangeInHeightProps = PropsWithChildren & {
  /** The transition settings for the height animation */
  transition?: Transition<any>;

  /** Optional additional CSS classes for the container */
  className?: string;
};

/**
 * A component that animates height changes of its children.
 *
 * @param children - The content whose height changes will be animated.
 * @param transition - The transition settings for the height animation.
 * @param className - Optional additional CSS classes for the container.
 *
 * @example
 * <AnimateChangeInHeight transition={{ duration: 0.2, ease: "easeOut" }}>
 *   <div>Your content here</div>
 * </AnimateChangeInHeight>
 */
export const AnimateChangeInHeight = ({
  children,
  transition = { duration: 0.1, damping: 0.2, ease: "easeIn" },
  className,
}: AnimateChangeInHeightProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | "auto">("auto");

  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        const observedHeight = entries[0]?.contentRect.height;
        setHeight(observedHeight ?? 0);
      });

      resizeObserver.observe(containerRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  return (
    <motion.div
      className={cn("overflow-hidden", className)}
      style={{ height }}
      animate={{ height }}
      transition={transition}
    >
      <div ref={containerRef}>{children}</div>
    </motion.div>
  );
};
